'use client';

import UploadIcon from '@/assets/svgs/text-editor/upload.svg';
import { Identity } from '@/containers/Identities/types';
import { useAddOptOutFile } from '@/containers/OptOutManager/api';
import { IdentitySelector } from '@/shared/components/IdentitySelector/IdentitySelector';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import classNames from 'classnames';
import { useCallback, useState } from 'react';

interface FileUploadTabProps {
  accountId: string;
  identities: Identity[];
  showIdentitySelector: boolean;
  onSuccess?: () => void;
  onClose: () => void;
}

export const FileUploadTab: React.FC<FileUploadTabProps> = ({
  accountId,
  identities,
  showIdentitySelector,
  onSuccess,
  onClose,
}) => {
  const { showSuccess, showError } = useToast();
  const addOptOutFileMutation = useAddOptOutFile();

  const [selectedIdentityId, setSelectedIdentityId] = useState<string>(
    identities.length === 1 ? identities[0].identity_id : ''
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState<{
    file?: string;
    identity?: string;
    reason?: string;
  }>({});

  const validateFile = useCallback((file: File): string | null => {
    const allowedTypes = [
      'text/plain',
      'text/csv',
      'application/csv',
      'application/zip',
      'application/x-zip-compressed',
    ];

    const allowedExtensions = ['.txt', '.csv', '.zip'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return 'Please upload a .txt, .csv, or .zip file';
    }

    // Max file size: 10MB
    if (file.size > 10 * 1024 * 1024) {
      return 'File size must be less than 10MB';
    }

    return null;
  }, []);

  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }

    if (showIdentitySelector && !selectedIdentityId) {
      newErrors.identity = 'Please select an identity';
    }

    if (!reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [selectedFile, selectedIdentityId, reason, showIdentitySelector]);

  const handleFileSelect = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;

      const file = fileArray[0];
      const error = validateFile(file);

      if (error) {
        showError(error);
        return;
      }

      setSelectedFile(file);
      if (errors.file) {
        setErrors((prev) => ({ ...prev, file: undefined }));
      }
    },
    [validateFile, showError, errors.file]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files);
      }
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files);
      }
    },
    [handleFileSelect]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm() || !selectedFile) {
        return;
      }

      setIsSubmitting(true);

      try {
        await addOptOutFileMutation.mutateAsync({
          account_id: accountId,
          identity_id: selectedIdentityId || identities[0]?.identity_id,
          file: selectedFile,
          reason: reason.trim(),
        });

        showSuccess(`Successfully uploaded ${selectedFile.name}`);
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Failed to upload file:', error);
        showError('Failed to upload file. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      selectedFile,
      accountId,
      selectedIdentityId,
      identities,
      reason,
      addOptOutFileMutation,
      showSuccess,
      showError,
      onSuccess,
      onClose,
    ]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Identity Selection - Only show if multiple identities */}
      {showIdentitySelector && (
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Identity</span>
          </label>
          <IdentitySelector
            identities={identities}
            value={selectedIdentityId}
            onChange={(e) => {
              setSelectedIdentityId(e.target.value);
              if (errors.identity) {
                setErrors((prev) => ({ ...prev, identity: undefined }));
              }
            }}
            currentAccountId={accountId}
            className={errors.identity ? 'select-error' : ''}
            placeholder="Select an identity"
          />
          {errors.identity && (
            <label className="label">
              <span className="text-error label-text-alt">{errors.identity}</span>
            </label>
          )}
        </div>
      )}

      {/* File Drop Zone */}
      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">Select File</span>
        </label>

        <div
          className={classNames(
            'relative flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors',
            {
              'border-primary bg-primary/5': isDragOver,
              'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500':
                !isDragOver,
              'border-error': errors.file,
            }
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}>
          <UploadIcon className="mb-4 h-12 w-12 text-gray-400" />
          <p className="mb-2 text-sm text-gray-500">
            <span className="font-semibold">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500">
            The list must contain a single text (.txt) or comma-separated value (.csv)
            file with one entry per line. This file may be zipped (.zip) if needed.
          </p>
          <p className="mt-1 text-xs text-gray-500">Maximum file size: 10MB</p>

          <input
            id="file-input"
            type="file"
            accept=".txt,.csv,.zip,text/plain,text/csv,application/csv,application/zip"
            onChange={handleInputChange}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </div>

        {errors.file && (
          <label className="label">
            <span className="text-error label-text-alt">{errors.file}</span>
          </label>
        )}
      </div>

      {/* Selected File Display */}
      {selectedFile && (
        <div className="dark:bg-base-200 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-200 text-xs text-gray-600 uppercase dark:bg-gray-700 dark:text-gray-200">
                {selectedFile.name.split('.').pop()}
              </div>
              <div>
                <div className="text-sm font-medium">{selectedFile.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                // Reset file input
                const input = document.getElementById('file-input') as HTMLInputElement;
                if (input) input.value = '';
              }}
              className="btn btn-ghost btn-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Reason Input */}
      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">Reason</span>
        </label>
        <textarea
          className={`textarea textarea-bordered w-full text-sm ${
            errors.reason ? 'textarea-error' : ''
          }`}
          placeholder="Enter the reason for these opt-outs..."
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (errors.reason) {
              setErrors((prev) => ({ ...prev, reason: undefined }));
            }
          }}
          rows={3}
        />
        {errors.reason && (
          <label className="label">
            <span className="text-error label-text-alt">{errors.reason}</span>
          </label>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
          disabled={isSubmitting}>
          {isSubmitting ? 'Uploading...' : 'Upload File'}
        </button>
      </div>
    </form>
  );
};
